// contentful.js

import { createClient } from "contentful";
import { CONTENT_TYPES } from "./constants/contentTypes";
import { fromUrlFriendly } from "./utils/urlHelpers";

// Validate required environment variables
const spaceId = process.env.REACT_APP_CONTENTFUL_SPACE_ID;
const accessToken = process.env.REACT_APP_CONTENTFUL_DELIVERY_ACCESS_TOKEN;

if (!spaceId || !accessToken) {
  console.error("Missing required Contentful credentials. Please check your .env file.");
  throw new Error("Missing required Contentful credentials. Please check your .env file.");
}

const client = createClient({
  space: spaceId,
  environment: process.env.REACT_APP_CONTENTFUL_ENVIRONMENT || "master",
  accessToken: accessToken,
});

export const fetchProducts = async (contentType = "cleaningProductData") => {
  try {
    console.log(`Fetching products for content type: ${contentType}`);
    const response = await client.getEntries({ 
      content_type: contentType,
      order: 'fields.productTitle' // Optional: order by product title
    });
    
    if (!response.items || response.items.length === 0) {
      console.warn(`No products found for content type: ${contentType}`);
      return [];
    }
    
    console.log(`Successfully fetched ${response.items.length} products for ${contentType}`);
    return response.items;
  } catch (error) {
    console.error(`Error fetching products for ${contentType}:`, error);
    if (error.status === 404) {
      console.error("Contentful space not found. Please verify your Space ID and Access Token.");
    } else if (error.status === 401) {
      console.error("Unauthorized. Please verify your Access Token.");
    } else if (error.status === 400) {
      console.error("Invalid content type. Please verify the content type exists in your Contentful space.");
    }
    return []; // Return empty array instead of throwing error
  }
};

export const fetchProductByTitle = async (productTitle, contentType) => {
  try {
    if (!productTitle) {
      console.error('fetchProductByTitle: No product title provided');
      return null;
    }

    if (!contentType) {
      console.error('fetchProductByTitle: No content type provided');
      return null;
    }

    console.log('fetchProductByTitle called with:', { productTitle, contentType });
    
    // Convert URL-friendly title back to normal title using the helper function
    let searchTitle;
    try {
      searchTitle = fromUrlFriendly(productTitle);
      console.log('Converted search title:', searchTitle);
    } catch (error) {
      console.error('Error converting URL title:', error);
      // Fallback to simple conversion if helper fails
      searchTitle = productTitle.replace(/-/g, ' ');
      console.log('Using fallback converted title:', searchTitle);
    }
    
    const response = await client.getEntries({ 
      content_type: contentType,
      'fields.productTitle[match]': searchTitle
    });
    console.log('Contentful response:', response);
    
    if (response.items && response.items.length > 0) {
      console.log('Found items:', response.items.length);
      // Find exact match first
      const exactMatch = response.items.find(item => 
        item.fields.productTitle.toLowerCase() === searchTitle.toLowerCase()
      );
      console.log('Exact match found:', !!exactMatch);
      
      // Return exact match if found, otherwise return first item
      const result = exactMatch || response.items[0];
      console.log('Returning product:', result?.fields?.productTitle);
      return result;
    }
    console.log('No items found in response');
    return null;
  } catch (error) {
    console.error('Error in fetchProductByTitle:', error);
    throw error;
  }
};

export const fetchProductById = async (productId, contentType) => {
  try {
    const response = await client.getEntry(productId);
    return response;
  } catch (error) {
    console.error('Error fetching product by ID:', error);
    throw error;
  }
};

export const fetchB2BProducts = async () => {
  try {
    const response = await client.getEntries({ content_type: "b2bCleaningProductData" });
    return response.items;
  } catch (error) {
    console.error("Error fetching B2B products:", error);
    if (error.status === 404) {
      console.error("Contentful space not found. Please verify your Space ID and Access Token.");
    } else if (error.status === 401) {
      console.error("Unauthorized. Please verify your Access Token.");
    }
    throw error;
  }
};

export const fetchBlogPosts = async () => {
  try {
    const response = await client.getEntries({
      content_type: CONTENT_TYPES.BLOG_POST,
      order: '-fields.publishDate' // Most recent first
    });
    return response.items;
  } catch (error) {
    console.error('Error fetching blog posts:', error);
    throw error;
  }
};

export const fetchBlogPostByTitle = async (title) => {
  try {
    // Convert URL-friendly title back to normal title
    const searchTitle = title.replace(/-/g, ' ');
    
    const response = await client.getEntries({
      content_type: CONTENT_TYPES.BLOG_POST,
      query: searchTitle
    });
    
    if (response.items && response.items.length > 0) {
      // Find the post that matches the title most closely
      const matchingPost = response.items.find(post => 
        post.fields.title.toLowerCase() === searchTitle.toLowerCase()
      );
      return matchingPost || response.items[0];
    }
    return null;
  } catch (error) {
    console.error('Error fetching blog post by title:', error);
    throw error;
  }
};

export const fetchNewsArticleByTitle = async (title) => {
  if (!title) {
    console.error('No title provided to fetchNewsArticleByTitle');
    return null;
  }

  try {
    // Try exact match first
    const response = await client.getEntries({
      content_type: 'newsArticle-2',
      'fields.title': title,
      limit: 1
    });

    if (response.items.length > 0) {
      return response.items[0];
    }

    // If no exact match, try partial match
    const partialResponse = await client.getEntries({
      content_type: 'newsArticle-2',
      'fields.title[match]': title,
      limit: 1
    });

    if (partialResponse.items.length > 0) {
      return partialResponse.items[0];
    }

    return null;
  } catch (error) {
    console.error('Error fetching article:', error);
    throw error;
  }
};

export default client;
